import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { StarIcon } from 'secondstep-components'
import { isFlagOn } from 'utils/featureFlags'
import GoalNotSetWarning from '../GoalNotSetWarning'
import {
  DateText,
  EditButton,
  GoalCompletedText,
  GoalContentHeaderWrapper,
  GoalContentWrapper,
  GoalMetricGroup,
  GoalNotSetWarningAlignRight,
  GoalSchoolwideDisabledMessage,
  GoalStatusBadge,
  GoalSubTitle,
  GoalTextLarge,
  GoalTitleWrapper,
  StyledDateRange,
  StyledEditIcon,
  StyledGoalHr,
  StyledProgressBar,
} from '../component.styles'
import {
  BTS_2026_FLAG,
  EDIT_BUTTON_TEST_ID,
  NO_SCHOOLWIDE_GOAL_MESSAGE,
} from '../constants'
import { formatShortDate, getLessonsGoalStatus } from '../goalHelpers'

const LessonsGoal = ({
  goalDate,
  isSingleSite,
  lessonCompletionGoalIsDisabled,
  lessonCompletionGoalValue,
  onOpenGoalsModal,
  onOpenGoalsModalFromWarning,
  programStartDate,
  showHeader,
  showMonitorViewOnly,
  sitePlaybookData,
}) => {
  const lessonsMarkedDone = sitePlaybookData?.lessons_marked_done || 0
  const lessonCompletionPercentage = Math.floor(
    (sitePlaybookData?.schoolwide_lesson_completion_percentage ?? 0) * 100,
  )
  const isGoalSet =
    lessonCompletionGoalIsDisabled ||
    (!!lessonCompletionGoalValue && !!programStartDate && !!goalDate)
  const goalStatusText = isGoalSet
    ? getLessonsGoalStatus({
        goalDate,
        schoolwideLessonCompletionPercentage:
          sitePlaybookData?.schoolwide_lesson_completion_percentage,
        lessonCompletionGoalValue,
      })
    : null
  const endDateInMs = goalDate ? new Date(goalDate).getTime() : null
  const programStartDateInMs = programStartDate
    ? new Date(programStartDate).getTime()
    : null

  return (
    <GoalContentWrapper
      isSingleSite={isSingleSite}
      selectedGoalIsCompleted={isGoalSet}
      showHeader={showHeader}
      showMonitorViewOnly={showMonitorViewOnly}
      useColumnLayout={true}
    >
      {isGoalSet && goalStatusText !== null && (
        <GoalStatusBadge goalStatusText={goalStatusText}>
          {goalStatusText !== 'Goal Missed' && (
            <StarIcon id="goal-status-icon" />
          )}
          <span>{goalStatusText}</span>
        </GoalStatusBadge>
      )}
      {isGoalSet && (
        <>
          <GoalContentHeaderWrapper>
            {!programStartDate && (
              <DateText>Target Date: {formatShortDate(goalDate)}</DateText>
            )}
            {programStartDate && (
              <StyledDateRange
                endDateInMs={endDateInMs}
                endDateTextFormatted={formatShortDate(goalDate)}
                progressBarView={true}
                startDateInMs={programStartDateInMs}
                startDateTextFormatted={formatShortDate(programStartDate)}
              />
            )}
            {isFlagOn([BTS_2026_FLAG]) && (
              <EditButton
                data-testid={EDIT_BUTTON_TEST_ID}
                onClick={onOpenGoalsModal}
              >
                <StyledEditIcon aria-label="Edit Goals" />
              </EditButton>
            )}
          </GoalContentHeaderWrapper>
          <StyledGoalHr showMonitorViewOnly={showMonitorViewOnly} />
        </>
      )}
      <Fragment key="goal-content">
        <GoalTitleWrapper>
          <GoalMetricGroup>
            <GoalTextLarge>{lessonCompletionPercentage}%</GoalTextLarge>
            <GoalSubTitle>Core Lessons Complete</GoalSubTitle>
          </GoalMetricGroup>
          <GoalCompletedText>
            {lessonsMarkedDone} of {sitePlaybookData?.total_lessons} lessons
          </GoalCompletedText>
        </GoalTitleWrapper>
        <StyledProgressBar
          goalPercentage={isGoalSet ? lessonCompletionGoalValue : undefined}
          hideProgressText={true}
          isGoalSet={isGoalSet}
          isSingleSite={isSingleSite}
          percentComplete={lessonCompletionPercentage}
          showGoalIndicator={isGoalSet}
        />
      </Fragment>
      {lessonCompletionGoalIsDisabled && (
        <GoalNotSetWarningAlignRight>
          <GoalSchoolwideDisabledMessage data-testid="goal-schoolwide-disabled-message">
            {NO_SCHOOLWIDE_GOAL_MESSAGE}
          </GoalSchoolwideDisabledMessage>
        </GoalNotSetWarningAlignRight>
      )}
      {!isGoalSet && !lessonCompletionGoalIsDisabled && (
        <GoalNotSetWarningAlignRight>
          <GoalNotSetWarning onOpenGoalsModal={onOpenGoalsModalFromWarning} />
        </GoalNotSetWarningAlignRight>
      )}
    </GoalContentWrapper>
  )
}

LessonsGoal.propTypes = {
  goalDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSingleSite: PropTypes.bool,
  lessonCompletionGoalIsDisabled: PropTypes.bool,
  lessonCompletionGoalValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onOpenGoalsModal: PropTypes.func,
  onOpenGoalsModalFromWarning: PropTypes.func,
  programStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showHeader: PropTypes.bool,
  showMonitorViewOnly: PropTypes.bool,
  sitePlaybookData: PropTypes.object,
}

export default LessonsGoal
